import { VAR_ASSIGN_RE } from '../Components/CodeEditor/ConfigurationTabs/hardwareParser';
import { isFrameworkBoolStreamType, isFrameworkFloatStreamType } from './micropythonStreamTypes';
import type { WidgetOptions } from '../Components/Dashboard/dashboardTypes';
import {
  BOOLEAN_WIDGET_TYPE,
  MECHANISM_WIDGET_TYPE,
  MOTOR_MULTI_PREFERRED_DEFAULT_METRICS,
  MOTOR_MULTI_WIDGET_TYPE,
  MULTI_AXIS_MAX_METRICS,
  MOTOR_WIDGET_TYPE,
  OTHER_WIDGET_TYPE,
  SENSOR_WIDGET_TYPE,
  isMotorStreamSensorKey,
  isElectronicSensorStreamSensorKey,
  motorBooleanMetricLabel,
  motorDrawerPortVariableTitle,
  motorFollowerMotorPortDisplayTitle,
  motorLeadMotorPortDisplayTitle,
  motorPortDisplayTitle,
  motorDrawerFloatMetricGroupTitle,
  motorMultiWidgetDataKey,
  motorStreamKeysForPortAndMetrics,
  motorWidgetDisplayTitle,
  otherWidgetDisplayTitle,
  otherClassNameFromStreamKey,
  otherMetricLabel,
  isOtherElectronicsStreamSensorKey,
  parseMotorMultiWidgetDataKey,
  resolveFrameworkBoolStreamSensorKeys,
  resolveFrameworkFloatStreamSensorKeys,
  sensorClassNameFromStreamKey,
  sensorMetricLabel,
  sensorWidgetDisplayTitle,
  streamMetricSlug,
  streamPortFromSensorKey,
  streamPrefixFromSensorKey,
} from '../Components/Graphs/sensor-utils';
import type { DriveUnitPortOccupancy } from '../redux/ApplicationSlice';
import type { FrameworkClass } from '../framework/framework.d';
import { FrameworkClassPartType } from '../framework/framework-enums';
import { FrameworkClassType } from '../framework/framework-types';
import { FrameworkClasses } from '../framework/frameworkClasses';
import { findFrameworkInitialization, getFrameworkDefaultValue } from '../framework/frameworkInitialization';
import { DRIVE_UNIT_PORT_PARAMETERS } from './driveUnitPortSelection';
import { frameworkClassNameFromElectronicKeyOrPrefix } from './electronicKeyUtils';
import { readConstructorKeywordValue, resolveConstructorDeclarationLineIndex, splitCodeLines } from './pythonSourceScanUtils';
import { isFollowerMotorPortElectronic, parseMechanismFollowerMotorsFromProjectCode } from './mechanismFollowerMotorUtils';

const MOTOR_PORT_PARAMETER_SUFFIX = `:${DRIVE_UNIT_PORT_PARAMETERS.MOTOR}`;
const SENSOR_PORT_PARAMETER_SUFFIX = `:${DRIVE_UNIT_PORT_PARAMETERS.SENSOR}`;
const FRAMEWORK_MOTOR_CLASSES = FrameworkClasses.filter((frameworkClass) => frameworkClass.partType === FrameworkClassPartType.Motors);

export type PortPickerEntry = {
  port: number;
  sectionTitle: string;
};

export type MotorPickerEntry = PortPickerEntry & {
  /** Drive-unit occupancy id (`class-variable:parameter`), unique per configured instance. */
  electronic: string;
  variableName: string;
  motorType: string;
  /** Shared user-facing port label (choose motors, drawer draggables, chart buttons). */
  displayTitle?: string;
};

function motorPickerPortLabel(entry: MotorPickerEntry): string {
  return entry.displayTitle ?? motorPortDisplayTitle(entry.port, entry.variableName);
}

function motorDrawerInstanceId(metric: string, entry: MotorPickerEntry, boolMetric = false): string {
  const prefix = boolMetric ? 'motor-drawer-bool' : 'motor-drawer';
  return `${prefix}-${metric}-${entry.electronic}`;
}

function mergeUniqueNonEmptyDisplayValues(values: readonly string[]): string {
  const unique = [...new Set(values.map((value) => value.trim()).filter((value) => value.length > 0))];
  if (unique.length === 0) return values[0]?.trim() ?? '';
  if (unique.length === 1) return unique[0];
  return unique.join(' / ');
}

function mergeUniqueValuesFirst(values: readonly string[]): string {
  const unique = [...new Set(values.filter((value) => value.length > 0))];
  return unique[0] ?? values[0] ?? '';
}

function buildPortKeyedMapFromEntries<T extends { port: number }>(
  entries: T[],
  pickValue: (entry: T) => string,
  mergeValues: (values: string[]) => string
): Map<number, string> {
  const valuesByPort = new Map<number, string[]>();
  for (const entry of entries) {
    const bucket = valuesByPort.get(entry.port) ?? [];
    bucket.push(pickValue(entry));
    valuesByPort.set(entry.port, bucket);
  }
  return new Map([...valuesByPort.entries()].map(([port, values]) => [port, mergeValues(values)]));
}

function resolveMotorPortOccupancyRow(portRows: DriveUnitPortOccupancy[], port: number, electronic?: string): DriveUnitPortOccupancy | undefined {
  if (electronic) {
    return portRows.find((entry) => entry.electronic === electronic);
  }
  return portRows.find((entry) => entry.driveUnitPort === port);
}

export type SensorPickerEntry = PortPickerEntry & {
  variableName: string;
  sensorType: string;
  /** Shared user-facing port label (choose sensors, drawer draggables, chart buttons). */
  displayTitle?: string;
};

function sensorPickerPortLabel(entry: SensorPickerEntry): string {
  return entry.displayTitle ?? motorPortDisplayTitle(entry.port, entry.variableName);
}

export type OtherPickerEntry = PortPickerEntry & {
  variableName: string;
  otherType: string;
  /** Shared user-facing port label (choose adapters, drawer draggables, chart buttons). */
  displayTitle?: string;
};

function otherPickerPortLabel(entry: OtherPickerEntry): string {
  return entry.displayTitle ?? motorPortDisplayTitle(entry.port, entry.variableName);
}

export type MotorDataPickerEntry = {
  metric: string;
  sensorKey: string;
};

export { sensorClassNameFromStreamKey };

export type ElectronicsDrawerStreamWidget = {
  id: string;
  type: string;
  dataKey: string;
  title: string;
  minH?: number;
  minW?: number;
  style?: string;
  options?: WidgetOptions;
};

export type ElectronicsDrawerSection = {
  electronic: string;
  variableName: string;
  deviceType: string;
  port: number;
  sectionTitle: string;
  widgets: ElectronicsDrawerStreamWidget[];
};

export type ElectronicsDrawerKind = {
  defaultClassName?: string;
  /** When set, only rows whose resolved framework class is in this set are included. */
  includedClassNames?: ReadonlySet<string>;
  /** When set, rows whose resolved framework class is in this set are omitted. */
  excludedClassNames?: ReadonlySet<string>;
  floatWidgetType: string;
  floatTitle: (sensorKey: string) => string;
  /** Dashboard widget `style` for float stream widgets (e.g. live number reading for `other`). */
  floatWidgetStyle?: string;
  booleanTitle: (sensorKey: string, variableName: string) => string;
  booleanVariableOptionKey: 'motorVariableName' | 'sensorVariableName' | 'otherVariableName';
};

function motorFloatTitleFromStreamKey(sensorKey: string): string {
  const metric = streamMetricSlug(sensorKey);
  const port = streamPortFromSensorKey(sensorKey) ?? 0;
  return motorWidgetDisplayTitle(metric, port);
}

function motorBooleanTitleFromStreamKey(sensorKey: string, variableName: string): string {
  const port = streamPortFromSensorKey(sensorKey) ?? 0;
  return motorPortDisplayTitle(port, variableName);
}

function sensorBooleanTitleFromStreamKey(sensorKey: string, variableName: string): string {
  void variableName;
  return sensorMetricLabel(streamPrefixFromSensorKey(sensorKey), streamMetricSlug(sensorKey));
}

const FRAMEWORK_ELECTRONIC_OTHER_CLASS_NAMES = new Set(
  FrameworkClasses.filter((c) => c.partType === FrameworkClassPartType.Other).map((c) => c.name)
);

/** Mechanism classes that appear in the motor data drawer (excludes DriveBase, which lives in the drivebase tab). */
export const FRAMEWORK_MECHANISM_CLASS_NAMES = new Set(
  FrameworkClasses.filter((c) => c.type === FrameworkClassType.Mechanism && c.uiPanel !== 'DriveBase').map((c) => c.name)
);

const MOTOR_EXCLUDED_CLASS_NAMES = new Set([...FRAMEWORK_ELECTRONIC_OTHER_CLASS_NAMES, ...FRAMEWORK_MECHANISM_CLASS_NAMES]);

export const MOTOR_ELECTRONICS_DRAWER_KIND: ElectronicsDrawerKind = {
  defaultClassName: 'Motor',
  excludedClassNames: MOTOR_EXCLUDED_CLASS_NAMES,
  floatWidgetType: MOTOR_WIDGET_TYPE,
  floatTitle: motorFloatTitleFromStreamKey,
  booleanTitle: motorBooleanTitleFromStreamKey,
  booleanVariableOptionKey: 'motorVariableName',
};

function electronicsDrawerKindForSensorClass(sensorClassName: string): ElectronicsDrawerKind {
  return {
    includedClassNames: new Set([sensorClassName]),
    floatWidgetType: SENSOR_WIDGET_TYPE,
    floatTitle: sensorWidgetDisplayTitle,
    booleanTitle: sensorBooleanTitleFromStreamKey,
    booleanVariableOptionKey: 'sensorVariableName',
  };
}

const FRAMEWORK_ELECTRONIC_SENSOR_CLASS_NAMES = new Set(
  FrameworkClasses.filter((c) => c.partType === FrameworkClassPartType.Sensors).map((c) => c.name)
);

/** All electronic sensors (`BeamBreakSensor`, `DistanceSensor`, `MagneticProximitySwitch`, …). */
export const ELECTRONIC_SENSOR_ELECTRONICS_DRAWER_KIND: ElectronicsDrawerKind = {
  includedClassNames: FRAMEWORK_ELECTRONIC_SENSOR_CLASS_NAMES,
  floatWidgetType: SENSOR_WIDGET_TYPE,
  floatTitle: sensorWidgetDisplayTitle,
  booleanTitle: sensorBooleanTitleFromStreamKey,
  booleanVariableOptionKey: 'sensorVariableName',
};

/** Other electronics on `motor_port` (e.g. `BitAdapter` live output percent). */
export const ELECTRONIC_OTHER_ELECTRONICS_DRAWER_KIND: ElectronicsDrawerKind = {
  includedClassNames: FRAMEWORK_ELECTRONIC_OTHER_CLASS_NAMES,
  floatWidgetType: OTHER_WIDGET_TYPE,
  floatTitle: otherWidgetDisplayTitle,
  floatWidgetStyle: 'number',
  booleanTitle: sensorBooleanTitleFromStreamKey,
  booleanVariableOptionKey: 'otherVariableName',
};

/** Non-drivebase mechanisms on `motor_port` (e.g. `RotationalMechanism`, `LinearMechanism`). */
export const MECHANISM_ELECTRONICS_DRAWER_KIND: ElectronicsDrawerKind = {
  includedClassNames: FRAMEWORK_MECHANISM_CLASS_NAMES,
  floatWidgetType: MECHANISM_WIDGET_TYPE,
  floatTitle: motorFloatTitleFromStreamKey,
  booleanTitle: motorBooleanTitleFromStreamKey,
  booleanVariableOptionKey: 'motorVariableName',
};

export function buildElectronicsSectionTitle(variableName: string, deviceType: string, port: number): string {
  if (!variableName.trim()) {
    return `${deviceType} - port: ${port}`;
  }
  return `${variableName} - ${deviceType} - port: ${port}`;
}

export function buildMotorSectionTitle(variableName: string, motorType: string, port: number): string {
  return buildElectronicsSectionTitle(variableName, motorType, port);
}

export function buildSensorSectionTitle(variableName: string, sensorType: string, port: number): string {
  return buildElectronicsSectionTitle(variableName, sensorType, port);
}

function parentNameFromElectronic(electronic: string): string {
  const id = electronic.slice(0, electronic.lastIndexOf(':'));
  const dashIdx = id.indexOf('-');
  return dashIdx >= 0 ? id.slice(dashIdx + 1) : id;
}

/** Lead `motor_port` rows and mechanism `add_motor` follower rows. */
export function isMotorPortOccupancyElectronic(electronic: string): boolean {
  return electronic.endsWith(MOTOR_PORT_PARAMETER_SUFFIX) || isFollowerMotorPortElectronic(electronic);
}

function isMotorPortRow(row: DriveUnitPortOccupancy): boolean {
  return isMotorPortOccupancyElectronic(row.electronic);
}

export function filterMotorPortOccupancyRows(driveUnitPorts: DriveUnitPortOccupancy[]): DriveUnitPortOccupancy[] {
  return driveUnitPorts.filter(isMotorPortRow);
}

function motorRows(driveUnitPorts: DriveUnitPortOccupancy[]): DriveUnitPortOccupancy[] {
  return filterMotorPortOccupancyRows(driveUnitPorts);
}

/** Empty motor variable name when the motor is embedded in a mechanism (no standalone Python name). */
export function mechanismEmbeddedMotorVariableName(): string {
  return '';
}

function resolveFrameworkConstructorKwargFromProjectCode(
  projectCode: string,
  variableName: string,
  frameworkClass: FrameworkClass,
  parameter: string
): string | null {
  const init = findFrameworkInitialization(frameworkClass, frameworkClass, parameter);
  const constructorArg = init?.constructor_arg;
  const fallback = init?.value != null ? String(init.value) : null;
  if (!constructorArg) return fallback;

  const lines = splitCodeLines(projectCode);
  const declLine = resolveConstructorDeclarationLineIndex(lines, variableName, {
    constructorClassName: frameworkClass.name,
  });
  if (declLine === null) return fallback;

  const rawValue = readConstructorKeywordValue(projectCode, declLine, constructorArg);
  return rawValue || fallback;
}

/** Maps a mechanism `motor_type` constructor value to its framework motor class via shared max_torque metadata. */
function resolveMotorClassForMechanism(mechanismClass: FrameworkClass, motorTypeRaw: string): FrameworkClass {
  const maxTorqueInit = findFrameworkInitialization(mechanismClass, mechanismClass, 'max_torque');
  const torqueForMotorType = maxTorqueInit?.defaultValueByParameter?.values[motorTypeRaw];

  if (torqueForMotorType != null) {
    const matchedMotorClass = FRAMEWORK_MOTOR_CLASSES.find((motorClass) => {
      const defaultTorque = getFrameworkDefaultValue(motorClass, motorClass, 'max_torque');
      return String(defaultTorque) === String(torqueForMotorType);
    });
    if (matchedMotorClass) return matchedMotorClass;
  }

  const motorPortInit = findFrameworkInitialization(mechanismClass, mechanismClass, 'motor_port');
  if (motorPortInit?.class) return motorPortInit.class;

  const childMotorClass = mechanismClass.children.find((child) => child.property === 'motor')?.class;
  if (childMotorClass) return childMotorClass;

  return FRAMEWORK_MOTOR_CLASSES[0];
}

/** Mechanism output metrics (not lead-motor shaft metrics) for multi-axis picker composition. */
const MECHANISM_OUTPUT_FLOAT_METRICS = new Set(['velocity', 'position']);

function resolveMechanismLeadMotorFloatStreamKeys(variableName: string, mechanismClassName: string, port: number, projectCode: string): string[] {
  const mechanismClass = FrameworkClasses.find((frameworkClass) => frameworkClass.name === mechanismClassName);
  if (!mechanismClass) return [];

  const motorTypeRaw = resolveFrameworkConstructorKwargFromProjectCode(projectCode, variableName, mechanismClass, 'motor_type') ?? '';
  const motorClass = resolveMotorClassForMechanism(mechanismClass, motorTypeRaw);
  return resolveFrameworkFloatStreamSensorKeys(motorClass, port).filter((key) => !MECHANISM_OUTPUT_FLOAT_METRICS.has(streamMetricSlug(key)));
}

function buildFollowerMotorPickerEntries(
  portRows: DriveUnitPortOccupancy[],
  projectCode: string,
  excludePorts: ReadonlySet<number>
): MotorPickerEntry[] {
  const followersByMechanismPort = new Map(
    parseMechanismFollowerMotorsFromProjectCode(projectCode).map((follower) => [`${follower.mechanismVariableName}:${follower.port}`, follower])
  );

  return motorRows(portRows)
    .filter((row) => isFollowerMotorPortElectronic(row.electronic))
    .filter((row) => !excludePorts.has(row.driveUnitPort))
    .flatMap((row) => {
      const mechanismVariableName = parentNameFromElectronic(row.electronic);
      const follower = followersByMechanismPort.get(`${mechanismVariableName}:${row.driveUnitPort}`);
      if (!follower) return [];

      const mechanismClass = FrameworkClasses.find((frameworkClass) => frameworkClass.name === follower.mechanismClassName);
      if (!mechanismClass) return [];

      const motorTypeRaw = resolveFrameworkConstructorKwargFromProjectCode(projectCode, mechanismVariableName, mechanismClass, 'motor_type') ?? '';
      const motorClass = resolveMotorClassForMechanism(mechanismClass, motorTypeRaw);
      const displayTitle = motorFollowerMotorPortDisplayTitle(row.driveUnitPort, mechanismVariableName, follower.name);

      return [
        {
          electronic: row.electronic,
          port: row.driveUnitPort,
          variableName: mechanismEmbeddedMotorVariableName(),
          motorType: motorClass.name,
          sectionTitle: displayTitle,
          displayTitle,
        },
      ];
    });
}

function mechanismMotorPickerEntry(mechanismEntry: MotorPickerEntry, projectCode: string): MotorPickerEntry | null {
  const mechanismClass = FrameworkClasses.find((frameworkClass) => frameworkClass.name === mechanismEntry.motorType);
  if (!mechanismClass) return null;

  const motorTypeRaw = resolveFrameworkConstructorKwargFromProjectCode(projectCode, mechanismEntry.variableName, mechanismClass, 'motor_type') ?? '';
  const motorClass = resolveMotorClassForMechanism(mechanismClass, motorTypeRaw);
  const motorVariableName = mechanismEmbeddedMotorVariableName();
  const displayTitle = motorLeadMotorPortDisplayTitle(mechanismEntry.port, mechanismEntry.variableName);

  return {
    electronic: mechanismEntry.electronic,
    port: mechanismEntry.port,
    variableName: motorVariableName,
    motorType: motorClass.name,
    sectionTitle: displayTitle,
    displayTitle,
  };
}

function buildMotorSectionFromPickerEntry(row: DriveUnitPortOccupancy, entry: MotorPickerEntry): ElectronicsDrawerSection {
  const frameworkClass = FrameworkClasses.find((frameworkClass) => frameworkClass.name === entry.motorType);
  const sectionBase = { electronic: row.electronic, port: entry.port };

  return {
    electronic: row.electronic,
    variableName: entry.variableName,
    deviceType: entry.motorType,
    port: entry.port,
    sectionTitle: entry.sectionTitle,
    widgets: buildElectronicsStreamWidgets(
      sectionBase,
      entry.variableName,
      MOTOR_ELECTRONICS_DRAWER_KIND,
      entry.motorType,
      resolveFrameworkFloatStreamSensorKeys(frameworkClass, entry.port),
      resolveFrameworkBoolStreamSensorKeys(frameworkClass, entry.port)
    ).map((widget) => (widget.type === BOOLEAN_WIDGET_TYPE ? { ...widget, title: motorPickerPortLabel(entry) } : widget)),
  };
}

function buildMotorElectronicsSectionForRow(row: DriveUnitPortOccupancy, projectCode: string): ElectronicsDrawerSection | null {
  const entry = getMotorPickerEntries([row], projectCode)[0];
  if (!entry) return null;
  return buildMotorSectionFromPickerEntry(row, entry);
}

function buildMotorElectronicsSections(portRows: DriveUnitPortOccupancy[], projectCode: string): ElectronicsDrawerSection[] {
  return motorRows(portRows).flatMap((row) => {
    const section = buildMotorElectronicsSectionForRow(row, projectCode);
    return section ? [section] : [];
  });
}

function buildClassNameByVariable(code: string): Map<string, string> {
  const byVariable = new Map<string, string>();
  if (!code.trim()) return byVariable;

  for (const line of code.split(/\r?\n/)) {
    const match = line.match(VAR_ASSIGN_RE);
    if (!match?.[1] || !match[2]) continue;
    byVariable.set(match[1], match[2]);
  }

  return byVariable;
}

function resolveDeviceClassName(
  electronic: string,
  variableName: string,
  classNameByVariable: Map<string, string>,
  defaultClassName?: string
): string | null {
  const fromCode = classNameByVariable.get(variableName) ?? frameworkClassNameFromElectronicKeyOrPrefix(electronic);
  if (fromCode) return fromCode;
  return defaultClassName ?? null;
}

function buildElectronicsStreamWidgets(
  section: { electronic: string; port: number },
  variableName: string,
  kind: ElectronicsDrawerKind,
  deviceType: string,
  floatSensorKeys: string[],
  boolSensorKeys: string[]
): ElectronicsDrawerStreamWidget[] {
  const floatWidgets = floatSensorKeys.map((sensorKey) => {
    const metric = streamMetricSlug(sensorKey);
    return {
      id: `${section.electronic}-${metric}`,
      type: kind.floatWidgetType,
      dataKey: sensorKey,
      title: kind.floatTitle(sensorKey),
      ...(kind.floatWidgetStyle ? { style: kind.floatWidgetStyle } : {}),
      ...(kind.floatWidgetType === SENSOR_WIDGET_TYPE ? { options: { sensorClassName: deviceType } } : {}),
    };
  });

  const booleanWidgets = boolSensorKeys.map((sensorKey) => {
    const metric = streamMetricSlug(sensorKey);
    return {
      id: `${section.electronic}-${metric}`,
      type: BOOLEAN_WIDGET_TYPE,
      dataKey: sensorKey,
      title: kind.booleanTitle(sensorKey, variableName),
      style: 'boolean',
      options: { [kind.booleanVariableOptionKey]: variableName },
      minH: 1,
      minW: 2,
    };
  });

  return [...floatWidgets, ...booleanWidgets];
}

/** Builds mechanism-data drawer sections from drive-unit port rows and framework stream definitions. */
export function buildElectronicsSections(
  portRows: DriveUnitPortOccupancy[],
  projectCode: string,
  kind: ElectronicsDrawerKind
): ElectronicsDrawerSection[] {
  const classNameByVariable = buildClassNameByVariable(projectCode);

  return portRows.flatMap((row) => {
    const variableName = parentNameFromElectronic(row.electronic);
    const deviceType = resolveDeviceClassName(row.electronic, variableName, classNameByVariable, kind.defaultClassName);
    if (!deviceType) return [];
    if (kind.includedClassNames && !kind.includedClassNames.has(deviceType)) return [];
    if (kind.excludedClassNames?.has(deviceType)) return [];

    const frameworkClass = FrameworkClasses.find((fc) => fc.name === deviceType);
    const floatSensorKeys = resolveFrameworkFloatStreamSensorKeys(frameworkClass, row.driveUnitPort);
    const boolSensorKeys = resolveFrameworkBoolStreamSensorKeys(frameworkClass, row.driveUnitPort);
    const sectionBase = { electronic: row.electronic, port: row.driveUnitPort };

    return [
      {
        electronic: row.electronic,
        variableName,
        deviceType,
        port: row.driveUnitPort,
        sectionTitle: buildElectronicsSectionTitle(variableName, deviceType, row.driveUnitPort),
        widgets: buildElectronicsStreamWidgets(sectionBase, variableName, kind, deviceType, floatSensorKeys, boolSensorKeys),
      },
    ];
  });
}

/** Motors with `motor_port` occupancy, including motors embedded in mechanisms. */
export function getMotorPickerEntries(driveUnitPorts: DriveUnitPortOccupancy[], projectCode: string): MotorPickerEntry[] {
  const standaloneEntries = buildElectronicsSections(motorRows(driveUnitPorts), projectCode, MOTOR_ELECTRONICS_DRAWER_KIND).map((section) => {
    const displayTitle = motorPortDisplayTitle(section.port, section.variableName);
    return {
      electronic: section.electronic,
      port: section.port,
      variableName: section.variableName,
      motorType: section.deviceType,
      sectionTitle: displayTitle,
      displayTitle,
    };
  });
  const standalonePorts = new Set(standaloneEntries.map((entry) => entry.port));

  const mechanismMotorEntries = getMechanismPickerEntries(driveUnitPorts, projectCode)
    .filter((entry) => !standalonePorts.has(entry.port))
    .map((entry) => mechanismMotorPickerEntry(entry, projectCode))
    .filter((entry): entry is MotorPickerEntry => entry !== null);

  const followerMotorEntries = buildFollowerMotorPickerEntries(driveUnitPorts, projectCode, standalonePorts);

  return [...standaloneEntries, ...mechanismMotorEntries, ...followerMotorEntries];
}

/** Non-drivebase mechanisms with `motor_port` occupancy (e.g. `RotationalMechanism`, `LinearMechanism`). */
export function getMechanismPickerEntries(driveUnitPorts: DriveUnitPortOccupancy[], projectCode: string): MotorPickerEntry[] {
  const leadMechanismRows = motorRows(driveUnitPorts).filter((row) => !isFollowerMotorPortElectronic(row.electronic));
  return buildElectronicsSections(leadMechanismRows, projectCode, MECHANISM_ELECTRONICS_DRAWER_KIND).map((section) => ({
    electronic: section.electronic,
    port: section.port,
    variableName: section.variableName,
    motorType: section.deviceType,
    sectionTitle: section.sectionTitle,
  }));
}

export type MechanismMetricGroupItem = {
  id: string;
  type: string;
  dataKey: string;
  /** Mechanism Python variable name (e.g. `lift`). */
  variableName: string;
  /** Framework class name (e.g. `RotationalMechanism`). Used to build i18n keys. */
  deviceType: string;
  /** Drive-unit port number. Used to disambiguate multiple instances of the same class. */
  port: number;
  minH: number;
  minW: number;
};

export type MechanismMetricGroup = {
  /** Stream metric slug (e.g. `velocity`, `position`). */
  metric: string;
  /** Framework mechanism class for every item in the group (e.g. `RotationalMechanism`). */
  deviceType: string;
  /** One entry per mechanism instance that exposes this metric. */
  items: MechanismMetricGroupItem[];
};

function mechanismMetricGroupMapKey(deviceType: string, metric: string): string {
  return `${deviceType}:${metric}`;
}

/**
 * Groups mechanism float stream widgets by framework class and metric so the drawer can render
 * one `CollapsibleDraggableData` per class/metric pair (e.g. RotationalMechanism velocity),
 * with one `DraggableComponent` per mechanism instance inside each group.
 */
export function buildMechanismDrawerMetricGroups(portRows: DriveUnitPortOccupancy[], projectCode: string): MechanismMetricGroup[] {
  const mechanismEntries = getMechanismPickerEntries(portRows, projectCode);
  const groupMap = new Map<string, MechanismMetricGroupItem[]>();

  for (const entry of mechanismEntries) {
    const row = portRows.find((r) => r.driveUnitPort === entry.port);
    if (!row) continue;

    const section = buildElectronicsSections([row], projectCode, MECHANISM_ELECTRONICS_DRAWER_KIND)[0];
    if (!section) continue;

    for (const widget of section.widgets) {
      if (widget.type !== MECHANISM_WIDGET_TYPE) continue;

      const metric = streamMetricSlug(widget.dataKey);
      const groupKey = mechanismMetricGroupMapKey(section.deviceType, metric);
      const existing = groupMap.get(groupKey) ?? [];
      groupMap.set(groupKey, [
        ...existing,
        {
          id: widget.id,
          type: widget.type,
          dataKey: widget.dataKey,
          variableName: entry.variableName,
          deviceType: section.deviceType,
          port: entry.port,
          minH: widget.minH ?? 3,
          minW: widget.minW ?? 4,
        },
      ]);
    }
  }

  return [...groupMap.entries()].map(([groupKey, items]) => {
    const metric = groupKey.slice(groupKey.indexOf(':') + 1);
    return { metric, deviceType: items[0].deviceType, items };
  });
}

export type DrawerCollapsibleWidget = {
  id: string;
  type: string;
  dataKey: string;
  title: string;
  minH?: number;
  minW?: number;
  style?: string;
  options?: WidgetOptions;
};

export type DrawerMetricCollapsibleGroup = {
  metric: string;
  /** React list key when multiple groups share the same metric slug (e.g. mechanism classes). */
  groupKey?: string;
  data: DrawerCollapsibleWidget[];
};

export type DrawerMetricTitleTranslate = (key: string, defaultValue: string) => string;

type DrawerWidgetCatalogSignature = Pick<DrawerCollapsibleWidget, 'id' | 'type' | 'dataKey'>;

/** Union of boolean metric slugs available on any configured motor port. */
export function getMotorDrawerBoolMetrics(portRows: DriveUnitPortOccupancy[], projectCode: string): string[] {
  const metrics: string[] = [];
  for (const entry of getMotorPickerEntries(portRows, projectCode)) {
    const row = portRows.find((r) => r.driveUnitPort === entry.port);
    if (!row) continue;
    const section = buildMotorElectronicsSectionForRow(row, projectCode);
    if (!section) continue;
    const frameworkClass = FrameworkClasses.find((fc) => fc.name === section.deviceType);
    for (const sensorKey of resolveFrameworkBoolStreamSensorKeys(frameworkClass, entry.port)) {
      const metric = streamMetricSlug(sensorKey);
      if (!metrics.includes(metric)) metrics.push(metric);
    }
  }
  return metrics;
}

/** Collapsible motor boolean-metric groups for the motor-data drawer (Motors section). */
export function buildMotorDrawerBooleanCollapsibleGroups(portRows: DriveUnitPortOccupancy[], projectCode: string): DrawerMetricCollapsibleGroup[] {
  const motorPickerEntries = getMotorPickerEntries(portRows, projectCode);
  if (motorPickerEntries.length === 0) return [];

  const boolMetrics = getMotorDrawerBoolMetrics(portRows, projectCode);
  const allPorts = motorPickerEntries.map((entry) => entry.port);

  return boolMetrics
    .map((metric) => {
      const baseDataKey = motorStreamKeyForPortAndMetric(portRows, projectCode, allPorts[0], metric);
      if (!baseDataKey) return null;

      const headerTitle = motorBooleanMetricLabel(metric);

      const headerItem: DrawerCollapsibleWidget = {
        id: `motor-drawer-bool-${metric}-all`,
        type: BOOLEAN_WIDGET_TYPE,
        dataKey: baseDataKey,
        title: headerTitle,
        minH: 2,
        minW: 4,
        options: { selectedMotorPorts: allPorts, allMotorPorts: true },
      };

      const individualItems: DrawerCollapsibleWidget[] = motorPickerEntries.map((entry) => {
        const dataKey = motorStreamKeyForPortAndMetric(portRows, projectCode, entry.port, metric) ?? baseDataKey;
        return {
          id: motorDrawerInstanceId(metric, entry, true),
          type: BOOLEAN_WIDGET_TYPE,
          dataKey,
          title: motorPickerPortLabel(entry),
          minH: 1,
          minW: 2,
          options: { selectedMotorPorts: [entry.port], motorVariableName: entry.variableName },
        };
      });

      return { metric, data: [headerItem, ...individualItems] };
    })
    .filter((group): group is DrawerMetricCollapsibleGroup => group !== null);
}

/** Collapsible motor float-metric groups for the motor-data drawer (Motors section). */
export function buildMotorDrawerMetricCollapsibleGroups(portRows: DriveUnitPortOccupancy[], projectCode: string): DrawerMetricCollapsibleGroup[] {
  const motorPickerEntries = getMotorPickerEntries(portRows, projectCode);
  if (motorPickerEntries.length === 0) return [];

  const floatMetrics = getMotorDrawerFloatMetrics(portRows, projectCode);
  const allPorts = motorPickerEntries.map((entry) => entry.port);

  return floatMetrics
    .map((metric) => {
      const baseDataKey = motorStreamKeyForPortAndMetric(portRows, projectCode, allPorts[0], metric);
      if (!baseDataKey) return null;

      const headerItem: DrawerCollapsibleWidget = {
        id: `motor-drawer-${metric}-all`,
        type: MOTOR_WIDGET_TYPE,
        dataKey: baseDataKey,
        title: motorDrawerFloatMetricGroupTitle(metric),
        minH: 3,
        minW: 4,
        options: { selectedMotorPorts: allPorts, allMotorPorts: true },
      };

      const individualItems: DrawerCollapsibleWidget[] = motorPickerEntries.map((entry) => ({
        id: motorDrawerInstanceId(metric, entry),
        type: MOTOR_WIDGET_TYPE,
        dataKey: motorStreamKeyForPortAndMetric(portRows, projectCode, entry.port, metric) ?? baseDataKey,
        title: motorPickerPortLabel(entry),
        minH: 3,
        minW: 4,
        options: { selectedMotorPorts: [entry.port] },
      }));

      return { metric, data: [headerItem, ...individualItems] };
    })
    .filter((group): group is DrawerMetricCollapsibleGroup => group !== null);
}

/** Collapsible mechanism metric groups for the motor-data drawer (Mechanisms section). */
export function buildMechanismDrawerMetricCollapsibleGroups(
  portRows: DriveUnitPortOccupancy[],
  projectCode: string,
  translate: DrawerMetricTitleTranslate
): DrawerMetricCollapsibleGroup[] {
  return buildMechanismDrawerMetricGroups(portRows, projectCode).map(({ metric, deviceType, items }) => {
    const mechanismClassName = deviceType.toLowerCase();
    const allPorts = items.map((item) => item.port);

    const headerItem: DrawerCollapsibleWidget = {
      ...items[0],
      id: `${mechanismClassName}-${metric}-all`,
      title: translate(`dashboard.draggableComponent.title.${mechanismClassName}.${metric}`, `${deviceType} ${metric}`),
      options: { selectedMotorPorts: allPorts, mechanismClassName, allMotorPorts: true },
    };

    const individualItems: DrawerCollapsibleWidget[] = items.map((item) => ({
      ...item,
      title: motorDrawerPortVariableTitle(item.port, item.variableName),
      options: { selectedMotorPorts: [item.port], mechanismClassName: item.deviceType.toLowerCase() },
    }));

    return { metric, groupKey: `${mechanismClassName}-${metric}`, data: [headerItem, ...individualItems] };
  });
}

export function parseMotorStreamSensorKey(sensorKey: string): { prefix: string; port: number; metric: string } | null {
  const match = sensorKey.match(/^((?:motor|mechanism))\.port(\d+)\.(.+)$/);
  if (!match) return null;
  const port = Number.parseInt(match[2], 10);
  if (!Number.isFinite(port)) return null;
  return { prefix: match[1], port, metric: match[3] };
}

/** Builds stream keys for the same metric/prefix as `baseSensorKey` on each drive-unit port. */
export function motorStreamKeysForPorts(baseSensorKey: string, ports: readonly number[]): string[] {
  const parsed = parseMotorStreamSensorKey(baseSensorKey);
  if (!parsed) return ports.length ? [baseSensorKey] : [];

  const uniquePorts = [...new Set(ports)].filter((port) => Number.isFinite(port)).sort((a, b) => a - b);
  return uniquePorts.map((port) => `${parsed.prefix}.port${port}.${parsed.metric}`);
}

/** Maps drive-unit port → motor Python variable name (e.g. `motor`). */
export function buildMotorVariableNameByPort(driveUnitPorts: DriveUnitPortOccupancy[], projectCode: string): Map<number, string> {
  return buildPortKeyedMapFromEntries(
    getMotorPickerEntries(driveUnitPorts, projectCode),
    (entry) => entry.variableName,
    mergeUniqueNonEmptyDisplayValues
  );
}

/** Maps drive-unit port → chart/drawer motor port label (standalone or lead-motor port title). */
export function buildMotorChartSeriesLabelByPort(driveUnitPorts: DriveUnitPortOccupancy[], projectCode: string): Map<number, string> {
  return buildPortKeyedMapFromEntries(
    getMotorPickerEntries(driveUnitPorts, projectCode),
    (entry) => motorPickerPortLabel(entry),
    mergeUniqueNonEmptyDisplayValues
  );
}

/** Maps drive-unit port → mechanism Python variable name (e.g. `lift`). */
export function buildMechanismVariableNameByPort(driveUnitPorts: DriveUnitPortOccupancy[], projectCode: string): Map<number, string> {
  return buildPortKeyedMapFromEntries(
    getMechanismPickerEntries(driveUnitPorts, projectCode),
    (entry) => entry.variableName,
    mergeUniqueNonEmptyDisplayValues
  );
}

/** Maps drive-unit port → mechanism drawer per-instance draggable title (`Port N: variableName`). */
export function buildMechanismDrawerPortLabelByPort(driveUnitPorts: DriveUnitPortOccupancy[], projectCode: string): Map<number, string> {
  return buildPortKeyedMapFromEntries(
    getMechanismPickerEntries(driveUnitPorts, projectCode),
    (entry) => motorDrawerPortVariableTitle(entry.port, entry.variableName),
    mergeUniqueNonEmptyDisplayValues
  );
}

/** Maps drive-unit port → framework motor class name (e.g. `HighSpeedMotor`). */
export function buildMotorClassNameByPort(driveUnitPorts: DriveUnitPortOccupancy[], projectCode: string): Map<number, string> {
  return buildPortKeyedMapFromEntries(getMotorPickerEntries(driveUnitPorts, projectCode), (entry) => entry.motorType, mergeUniqueValuesFirst);
}

/** Maps drive-unit port → framework mechanism class name (e.g. `RotationalMechanism`). */
export function buildMechanismClassNameByPort(driveUnitPorts: DriveUnitPortOccupancy[], projectCode: string): Map<number, string> {
  return buildPortKeyedMapFromEntries(getMechanismPickerEntries(driveUnitPorts, projectCode), (entry) => entry.motorType, mergeUniqueValuesFirst);
}

export function defaultSelectedMotorPorts(_baseSensorKey: string, availablePorts: readonly number[]): number[] {
  return [...availablePorts].filter((port) => Number.isFinite(port)).sort((a, b) => a - b);
}

/** Drive-unit ports referenced by motor stream keys (optionally filtered to one metric). */
export function motorPortsFromStreamKeys(sensorKeys: readonly string[], baseDataKey?: string): number[] {
  const metric = baseDataKey?.trim() ? streamMetricSlug(baseDataKey) : undefined;
  const ports = sensorKeys
    .filter((key) => {
      if (!isMotorStreamSensorKey(key)) return false;
      if (metric === undefined) return true;
      return streamMetricSlug(key) === metric;
    })
    .map((key) => streamPortFromSensorKey(key))
    .filter((port): port is number => port !== null && Number.isFinite(port));
  return [...new Set(ports)].sort((a, b) => a - b);
}

const MOTOR_STREAM_PORT_TEMPLATE_RE = /\.port\{port\}\.(.+)$/;

/** Canonical display order of float metrics on `motor.port{port}.*` stream templates. */
export function getFrameworkMotorFloatMetricOrder(): string[] {
  const highSpeedMotor = FrameworkClasses.find((frameworkClass) => frameworkClass.name === 'HighSpeedMotor');
  if (!highSpeedMotor?.streamData?.length) return [];

  const metrics: string[] = [];
  for (const entry of highSpeedMotor.streamData) {
    if (!isFrameworkFloatStreamType(entry.type)) continue;
    const match = entry.name.match(MOTOR_STREAM_PORT_TEMPLATE_RE);
    if (match?.[1]) metrics.push(match[1]);
  }
  return metrics;
}

/** Float stream metrics available on at least one configured motor in the project. */
export function getMotorDrawerFloatMetrics(portRows: DriveUnitPortOccupancy[], projectCode: string): string[] {
  const sections = buildMotorElectronicsSections(portRows, projectCode);

  const available = new Set<string>();
  for (const section of sections) {
    for (const widget of section.widgets) {
      if (widget.type === MOTOR_WIDGET_TYPE) {
        available.add(streamMetricSlug(widget.dataKey));
      }
    }
  }

  const order = getFrameworkMotorFloatMetricOrder();
  const extras = [...available].filter((metric) => !order.includes(metric)).sort();
  return [...order.filter((metric) => available.has(metric)), ...extras];
}

/** Which stream namespace to resolve for a drive-unit port (`motor.portN.*` vs `mechanism.portN.*`). */
export type MotorStreamResolutionKind = 'motor' | 'mechanism';

/** Project context for resolving mixed motor/mechanism stream keys from program code. */
export type StreamProjectContext = {
  portRows: DriveUnitPortOccupancy[];
  projectCode: string;
};

function streamKeyFromElectronicsSection(
  section: ElectronicsDrawerSection | null | undefined,
  metric: string,
  widgetTypes: readonly string[]
): string | null {
  const widget = section?.widgets.find((entry) => widgetTypes.includes(entry.type) && streamMetricSlug(entry.dataKey) === metric);
  return widget?.dataKey ?? null;
}

/** Stream key for one metric on a drive-unit port, or null when unavailable. */
export function motorStreamKeyForPortAndMetric(
  portRows: DriveUnitPortOccupancy[],
  projectCode: string,
  port: number,
  metric: string,
  kind: MotorStreamResolutionKind = 'motor',
  mechanismEntry?: MotorPickerEntry | null
): string | null {
  const row = portRows.find((entry) => entry.driveUnitPort === port);
  if (!row) return null;

  if (kind === 'mechanism') {
    const mechanismSection = buildElectronicsSections([row], projectCode, MECHANISM_ELECTRONICS_DRAWER_KIND)[0];
    const mechanismKey = streamKeyFromElectronicsSection(mechanismSection, metric, [MECHANISM_WIDGET_TYPE]);
    if (mechanismKey) return mechanismKey;

    const entry = mechanismEntry ?? getMechanismPickerEntries(portRows, projectCode).find((pickerEntry) => pickerEntry.port === port);
    if (!entry) return null;

    const leadMotorKeys = resolveMechanismLeadMotorFloatStreamKeys(entry.variableName, entry.motorType, port, projectCode);
    return leadMotorKeys.find((key) => streamMetricSlug(key) === metric) ?? null;
  }

  const motorSection = buildMotorElectronicsSectionForRow(row, projectCode);
  return streamKeyFromElectronicsSection(motorSection, metric, [MOTOR_WIDGET_TYPE, BOOLEAN_WIDGET_TYPE]);
}

/** Velocity stream key for a motor on `motor_port`, or null when unavailable. */
export function motorVelocityStreamKeyForPort(portRows: DriveUnitPortOccupancy[], projectCode: string, port: number): string | null {
  return motorStreamKeyForPortAndMetric(portRows, projectCode, port, 'velocity');
}

/** Resolved stream keys for `metric` on each selected drive-unit port. */
export function motorStreamKeysForSelectedPorts(
  portRows: DriveUnitPortOccupancy[],
  projectCode: string,
  metric: string,
  ports: readonly number[],
  kind: MotorStreamResolutionKind = 'motor'
): string[] {
  const uniquePorts = [...new Set(ports)].filter((port) => Number.isFinite(port)).sort((a, b) => a - b);
  return uniquePorts
    .map((port) => motorStreamKeyForPortAndMetric(portRows, projectCode, port, metric, kind))
    .filter((key): key is string => Boolean(key));
}

/** Stream prefix (`motor` or `mechanism`) for a drive-unit port. */
export function motorStreamPrefixForPort(
  portRows: DriveUnitPortOccupancy[],
  projectCode: string,
  port: number,
  kind: MotorStreamResolutionKind = 'motor'
): string {
  const velocityKey = motorStreamKeyForPortAndMetric(portRows, projectCode, port, 'velocity', kind);
  if (!velocityKey) return kind;
  return parseMotorStreamSensorKey(velocityKey)?.prefix ?? kind;
}

/** Float stream metrics for a multi-axis chart on one drive-unit port. */
export function getMultiAxisDataPickerEntries(
  port: number,
  streamPrefix: string,
  driveUnitPorts: DriveUnitPortOccupancy[],
  projectCode: string
): MotorDataPickerEntry[] {
  if (streamPrefix === 'mechanism') {
    return getMechanismMultiAxisDataPickerEntries(port, driveUnitPorts, projectCode);
  }
  return getMotorDataPickerEntries(port, driveUnitPorts, projectCode);
}

/** Float stream metrics for a mechanism multi-axis chart on one drive-unit port. */
export function getMechanismMultiAxisDataPickerEntries(
  port: number,
  driveUnitPorts: DriveUnitPortOccupancy[],
  projectCode: string
): MotorDataPickerEntry[] {
  const mechanismEntry = getMechanismPickerEntries(driveUnitPorts, projectCode).find((entry) => entry.port === port);
  if (!mechanismEntry) return [];

  const mechanismClass = FrameworkClasses.find((frameworkClass) => frameworkClass.name === mechanismEntry.motorType);
  if (!mechanismClass) return [];

  const mechanismKeys = resolveFrameworkFloatStreamSensorKeys(mechanismClass, port);
  const leadMotorKeys = resolveMechanismLeadMotorFloatStreamKeys(mechanismEntry.variableName, mechanismEntry.motorType, port, projectCode);

  const seenMetrics = new Set<string>();
  const entries: MotorDataPickerEntry[] = [];
  for (const sensorKey of [...mechanismKeys, ...leadMotorKeys]) {
    const metric = streamMetricSlug(sensorKey);
    if (seenMetrics.has(metric)) continue;
    seenMetrics.add(metric);
    entries.push({ metric, sensorKey });
  }
  return entries;
}

/** Float stream metrics available for a motor on one drive-unit port. */
export function getMotorDataPickerEntries(port: number, driveUnitPorts: DriveUnitPortOccupancy[], projectCode: string): MotorDataPickerEntry[] {
  const motorEntry = getMotorPickerEntries(driveUnitPorts, projectCode).find((entry) => entry.port === port);
  const mechanismEntry = getMechanismPickerEntries(driveUnitPorts, projectCode).find((entry) => entry.port === port);
  const pickerEntry = motorEntry ?? mechanismEntry;
  if (!pickerEntry) return [];

  const frameworkClass = FrameworkClasses.find((fc) => fc.name === pickerEntry.motorType);
  const floatSensorKeys = resolveFrameworkFloatStreamSensorKeys(frameworkClass, port);
  return floatSensorKeys.map((sensorKey) => ({
    metric: streamMetricSlug(sensorKey),
    sensorKey,
  }));
}

/** Builds stream keys for a multi-metric motor chart on one port. */
export function motorMultiStreamKeysForMetrics(
  dataKey: string,
  port: number,
  metrics: readonly string[],
  projectContext?: StreamProjectContext
): string[] {
  const parsed = parseMotorMultiWidgetDataKey(dataKey);
  const prefix = parsed?.prefix ?? 'motor';
  const resolvedPort = parsed?.port ?? port;

  if (projectContext) {
    const kind: MotorStreamResolutionKind = prefix === 'mechanism' ? 'mechanism' : 'motor';
    return motorStreamKeysForProjectPortAndMetrics(projectContext.portRows, projectContext.projectCode, resolvedPort, metrics, kind);
  }

  return motorStreamKeysForPortAndMetrics(resolvedPort, prefix, metrics);
}

/** Resolves framework stream keys for metrics on one port (handles mixed `motor` / `mechanism` namespaces). */
export function motorStreamKeysForProjectPortAndMetrics(
  portRows: DriveUnitPortOccupancy[],
  projectCode: string,
  port: number,
  metrics: readonly string[],
  kind: MotorStreamResolutionKind
): string[] {
  const uniqueMetrics = [...new Set(metrics)].filter((metric) => metric.length > 0);
  const mechanismEntry = kind === 'mechanism' ? getMechanismPickerEntries(portRows, projectCode).find((entry) => entry.port === port) : undefined;
  return uniqueMetrics
    .map((metric) => motorStreamKeyForPortAndMetric(portRows, projectCode, port, metric, kind, mechanismEntry))
    .filter((key): key is string => Boolean(key));
}

/** Keeps at least one metric and at most {@link MULTI_AXIS_MAX_METRICS} selections. */
export function clampMotorMultiMetrics(metrics: readonly string[], availableMetrics: readonly string[]): string[] {
  const unique = [...new Set(metrics)].filter((metric) => availableMetrics.includes(metric));
  if (!unique.length) {
    return availableMetrics.length ? [availableMetrics[0]] : [];
  }
  return unique.slice(0, MULTI_AXIS_MAX_METRICS);
}

/** Default metrics for a new multi-axis chart from stream prefix and available float metrics. */
export function defaultMotorMultiMetrics(availableMetrics: readonly string[], streamPrefix: string): string[] {
  if (streamPrefix === 'mechanism') {
    const preferred = [...new Set([...MOTOR_MULTI_PREFERRED_DEFAULT_METRICS])].filter((metric) => availableMetrics.includes(metric));
    if (preferred.length) return clampMotorMultiMetrics(preferred, availableMetrics);
    return clampMotorMultiMetrics(availableMetrics, availableMetrics);
  }
  const preferred = MOTOR_MULTI_PREFERRED_DEFAULT_METRICS.filter((metric) => availableMetrics.includes(metric));
  if (preferred.length) return preferred;
  return clampMotorMultiMetrics(availableMetrics.slice(0, MULTI_AXIS_MAX_METRICS), availableMetrics);
}

export function defaultSelectedMotorMetrics(dataKey: string, availableMetrics: readonly string[], fromOptions?: string[]): string[] {
  if (fromOptions?.length) {
    return clampMotorMultiMetrics(fromOptions, availableMetrics);
  }
  const prefix = parseMotorMultiWidgetDataKey(dataKey)?.prefix ?? 'motor';
  return defaultMotorMultiMetrics(availableMetrics, prefix);
}

function buildPortMultiAxisWidget(
  portRows: DriveUnitPortOccupancy[],
  projectCode: string,
  electronic: string,
  drawerKind: typeof MOTOR_ELECTRONICS_DRAWER_KIND | typeof MECHANISM_ELECTRONICS_DRAWER_KIND,
  widgetIdSuffix: string
): ElectronicsDrawerStreamWidget | null {
  const row = portRows.find((entry) => entry.electronic === electronic);
  if (!row) return null;

  const port = row.driveUnitPort;

  const section =
    drawerKind === MOTOR_ELECTRONICS_DRAWER_KIND
      ? buildMotorElectronicsSectionForRow(row, projectCode)
      : buildElectronicsSections([row], projectCode, drawerKind)[0];
  if (!section) return null;

  const resolutionKind: MotorStreamResolutionKind = drawerKind === MOTOR_ELECTRONICS_DRAWER_KIND ? 'motor' : 'mechanism';
  const prefix = motorStreamPrefixForPort(portRows, projectCode, port, resolutionKind);
  const availableMetrics = getMultiAxisDataPickerEntries(port, prefix, portRows, projectCode).map((entry) => entry.metric);
  const selectedMotorMetrics = defaultMotorMultiMetrics(availableMetrics, prefix);

  const options: WidgetOptions = {
    selectedMotorPort: port,
    selectedMotorMetrics,
    motorVariableName: section.variableName,
  };

  if (drawerKind === MECHANISM_ELECTRONICS_DRAWER_KIND) {
    options.mechanismClassName = section.deviceType.toLowerCase();
  }

  return {
    id: `${section.electronic}-${widgetIdSuffix}`,
    type: MOTOR_MULTI_WIDGET_TYPE,
    dataKey: motorMultiWidgetDataKey(port, prefix),
    title: section.variableName,
    minH: 4,
    minW: 4,
    options,
  };
}

/** Returns the multi-axis widget for a single mechanism port (for the Advanced drawer section). */
export function buildMechanismMultiAxisWidget(
  portRows: DriveUnitPortOccupancy[],
  projectCode: string,
  port: number,
  electronic?: string
): ElectronicsDrawerStreamWidget | null {
  const row = resolveMotorPortOccupancyRow(portRows, port, electronic);
  if (!row) return null;
  return buildPortMultiAxisWidget(portRows, projectCode, row.electronic, MECHANISM_ELECTRONICS_DRAWER_KIND, 'mechanism-multi');
}

/** Returns the multi-axis widget for a single motor port (for the Advanced drawer section). */
export function buildMotorMultiAxisWidget(
  portRows: DriveUnitPortOccupancy[],
  projectCode: string,
  port: number,
  electronic?: string
): ElectronicsDrawerStreamWidget | null {
  const row = resolveMotorPortOccupancyRow(portRows, port, electronic);
  if (!row) return null;
  return buildPortMultiAxisWidget(portRows, projectCode, row.electronic, MOTOR_ELECTRONICS_DRAWER_KIND, 'multi');
}

export type MotorDataMultiAxisDrawerItem = {
  key: string;
  port: number;
  widget: ElectronicsDrawerStreamWidget;
  titleI18nKey: string;
  titleDefault: string;
};

function multiAxisDrawerItemsForPickerEntries(
  pickerEntries: MotorPickerEntry[],
  buildWidget: (entry: MotorPickerEntry) => ElectronicsDrawerStreamWidget | null,
  titleI18nKey: string,
  titleDefault: string
): MotorDataMultiAxisDrawerItem[] {
  return pickerEntries
    .map((entry) => ({ entry, widget: buildWidget(entry) }))
    .filter((item): item is { entry: MotorPickerEntry; widget: ElectronicsDrawerStreamWidget } => item.widget !== null)
    .map(({ entry, widget }) => ({
      key: widget.id,
      port: entry.port,
      widget,
      titleI18nKey,
      titleDefault,
    }));
}

/** Multi-axis motor and mechanism widgets for the Advanced drawer section. */
export function buildMotorDataDrawerMultiAxisItems(portRows: DriveUnitPortOccupancy[], projectCode: string): MotorDataMultiAxisDrawerItem[] {
  return [
    ...multiAxisDrawerItemsForPickerEntries(
      getMotorPickerEntries(portRows, projectCode),
      (entry) => buildPortMultiAxisWidget(portRows, projectCode, entry.electronic, MOTOR_ELECTRONICS_DRAWER_KIND, 'multi'),
      'dashboard.draggableComponent.title.motor.multiAxis',
      'Port {{port}}: multi-axis motor data'
    ),
    ...multiAxisDrawerItemsForPickerEntries(
      getMechanismPickerEntries(portRows, projectCode),
      (entry) => buildPortMultiAxisWidget(portRows, projectCode, entry.electronic, MECHANISM_ELECTRONICS_DRAWER_KIND, 'mechanism-multi'),
      'dashboard.draggableComponent.title.mechanism.multiAxis',
      'Port {{port}}: multi-axis mechanism data'
    ),
  ];
}

function drawerWidgetCatalogEntries(widgets: readonly DrawerWidgetCatalogSignature[]): DrawerWidgetCatalogSignature[] {
  return widgets.map(({ id, type, dataKey }) => ({ id, type, dataKey }));
}

/** Flat widget catalog for motor-data drawer draggables (used by tests and layout sync). */
export function buildMotorDataDrawerWidgetCatalog(
  portRows: DriveUnitPortOccupancy[],
  projectCode: string,
  translate: DrawerMetricTitleTranslate = (_key, defaultValue) => defaultValue
): DrawerWidgetCatalogSignature[] {
  const catalog: DrawerWidgetCatalogSignature[] = [];

  for (const group of buildMechanismDrawerMetricCollapsibleGroups(portRows, projectCode, translate)) {
    catalog.push(...drawerWidgetCatalogEntries(group.data));
  }

  for (const group of buildMotorDrawerMetricCollapsibleGroups(portRows, projectCode)) {
    catalog.push(...drawerWidgetCatalogEntries(group.data));
  }

  for (const group of buildMotorDrawerBooleanCollapsibleGroups(portRows, projectCode)) {
    catalog.push(...drawerWidgetCatalogEntries(group.data));
  }

  for (const { widget } of buildMotorDataDrawerMultiAxisItems(portRows, projectCode)) {
    catalog.push({ id: widget.id, type: widget.type, dataKey: widget.dataKey });
  }

  return catalog;
}

/** Returns the boolean stream widgets for a single motor port (for the per-motor drawer section). */
export function buildMotorDrawerPerMotorWidgets(
  portRows: DriveUnitPortOccupancy[],
  projectCode: string,
  port: number
): ElectronicsDrawerStreamWidget[] {
  const row = portRows.find((entry) => entry.driveUnitPort === port);
  if (!row) return [];

  const section = buildMotorElectronicsSectionForRow(row, projectCode);
  if (!section) return [];

  return section.widgets
    .filter((widget) => widget.type === BOOLEAN_WIDGET_TYPE)
    .map((widget) => ({
      id: widget.id,
      type: BOOLEAN_WIDGET_TYPE,
      dataKey: widget.dataKey,
      title: widget.title,
      style: 'boolean',
      options: { motorVariableName: section.variableName },
      minH: widget.minH ?? 1,
      minW: widget.minW ?? 2,
    }));
}

export function defaultSelectedSensorPorts(baseSensorKey: string, availablePorts: readonly number[]): number[] {
  return defaultSelectedDriveUnitPorts(baseSensorKey, availablePorts);
}

/** Drive-unit ports referenced by electronic sensor stream keys (optionally filtered to one metric). */
export function sensorPortsFromStreamKeys(sensorKeys: readonly string[], baseDataKey?: string): number[] {
  const metric = baseDataKey?.trim() ? streamMetricSlug(baseDataKey) : undefined;
  const ports = sensorKeys
    .filter((key) => {
      if (!isElectronicSensorStreamSensorKey(key)) return false;
      if (metric === undefined) return true;
      return streamMetricSlug(key) === metric;
    })
    .map((key) => streamPortFromSensorKey(key))
    .filter((port): port is number => port !== null && Number.isFinite(port));
  return [...new Set(ports)].sort((a, b) => a - b);
}

/** Maps drive-unit port → sensor drawer/chart port label for one sensor class. */
export function buildSensorChartSeriesLabelByPort(
  driveUnitPorts: DriveUnitPortOccupancy[],
  projectCode: string,
  sensorClassName: string
): Map<number, string> {
  return new Map(
    getSensorPickerEntries(driveUnitPorts, projectCode, sensorClassName).map((entry) => [
      entry.port,
      entry.displayTitle ?? motorPortDisplayTitle(entry.port, entry.variableName),
    ])
  );
}

function defaultSelectedDriveUnitPorts(baseSensorKey: string, availablePorts: readonly number[]): number[] {
  const fromKey = streamPortFromSensorKey(baseSensorKey);
  if (fromKey !== null && availablePorts.includes(fromKey)) return [fromKey];
  return availablePorts.length ? [availablePorts[0]] : [];
}

export function parseElectronicSensorStreamKey(sensorKey: string): { prefix: string; port: number; metric: string } | null {
  if (!sensorClassNameFromStreamKey(sensorKey)) return null;
  const port = streamPortFromSensorKey(sensorKey);
  if (port === null) return null;
  const prefix = sensorKey.split('.')[0] ?? '';
  return { prefix, port, metric: streamMetricSlug(sensorKey) };
}

/** Builds stream keys for the same metric/prefix as `baseSensorKey` on each drive-unit port. */
export function sensorStreamKeysForPorts(baseSensorKey: string, ports: readonly number[]): string[] {
  const parsed = parseElectronicSensorStreamKey(baseSensorKey);
  if (!parsed) return ports.length ? [baseSensorKey] : [];

  const uniquePorts = [...new Set(ports)].filter((port) => Number.isFinite(port)).sort((a, b) => a - b);
  return uniquePorts.map((port) => `${parsed.prefix}.port${port}.${parsed.metric}`);
}

/** Resolves persisted sensor ports and stream keys when dragging a sensor/boolean widget onto the dashboard. */
export function resolveElectronicSensorWidgetDrop(
  dataKeyStr: string,
  widgetOptions: WidgetOptions | undefined
): { dropOptions: Pick<WidgetOptions, 'selectedSensorPorts' | 'sensorClassName'>; sensorKeys: string[] } | null {
  const parsed = parseElectronicSensorStreamKey(dataKeyStr);
  const sensorClassName = widgetOptions?.sensorClassName ?? sensorClassNameFromStreamKey(dataKeyStr);
  if (!parsed || !sensorClassName) return null;

  const portsFromOptions = widgetOptions?.selectedSensorPorts?.filter((port): port is number => Number.isFinite(port));
  const selectedPorts = portsFromOptions?.length ? [...new Set(portsFromOptions)].sort((a, b) => a - b) : [parsed.port];

  return {
    dropOptions: { selectedSensorPorts: selectedPorts, sensorClassName },
    sensorKeys: sensorStreamKeysForPorts(dataKeyStr, selectedPorts),
  };
}

/** `sensor_port` rows for one framework sensor class (e.g. all BeamBreakSensor instances). */
export function getSensorPickerEntries(driveUnitPorts: DriveUnitPortOccupancy[], projectCode: string, sensorClassName: string): SensorPickerEntry[] {
  return buildElectronicsSections(
    driveUnitPorts.filter((row) => row.electronic.endsWith(SENSOR_PORT_PARAMETER_SUFFIX)),
    projectCode,
    electronicsDrawerKindForSensorClass(sensorClassName)
  ).map((section) => {
    const displayTitle = motorPortDisplayTitle(section.port, section.variableName);
    return {
      port: section.port,
      variableName: section.variableName,
      sensorType: section.deviceType,
      sectionTitle: displayTitle,
      displayTitle,
    };
  });
}

const SENSOR_STREAM_PORT_TEMPLATE_RE = /\.port\{port\}\.(.+)$/;

function getFrameworkSensorFloatMetricOrder(sensorClassName: string): string[] {
  const frameworkClass = FrameworkClasses.find((fc) => fc.name === sensorClassName);
  if (!frameworkClass?.streamData?.length) return [];

  const metrics: string[] = [];
  for (const entry of frameworkClass.streamData) {
    if (!isFrameworkFloatStreamType(entry.type)) continue;
    const match = entry.name.match(SENSOR_STREAM_PORT_TEMPLATE_RE);
    if (match?.[1]) metrics.push(match[1]);
  }
  return metrics;
}

function getFrameworkSensorBoolMetricOrder(sensorClassName: string): string[] {
  const frameworkClass = FrameworkClasses.find((fc) => fc.name === sensorClassName);
  if (!frameworkClass?.streamData?.length) return [];

  const metrics: string[] = [];
  for (const entry of frameworkClass.streamData) {
    if (!isFrameworkBoolStreamType(entry.type)) continue;
    const match = entry.name.match(SENSOR_STREAM_PORT_TEMPLATE_RE);
    if (match?.[1]) metrics.push(match[1]);
  }
  return metrics;
}

function sensorPortRows(portRows: DriveUnitPortOccupancy[]): DriveUnitPortOccupancy[] {
  return portRows.filter((row) => row.electronic.endsWith(SENSOR_PORT_PARAMETER_SUFFIX));
}

/** Framework sensor classes with at least one configured `sensor_port` row in project code. */
export function getConfiguredSensorClassNames(portRows: DriveUnitPortOccupancy[], projectCode: string): string[] {
  const configured = new Set(
    buildElectronicsSections(sensorPortRows(portRows), projectCode, ELECTRONIC_SENSOR_ELECTRONICS_DRAWER_KIND).map((section) => section.deviceType)
  );

  return FrameworkClasses.filter(
    (frameworkClass) => frameworkClass.partType === FrameworkClassPartType.Sensors && configured.has(frameworkClass.name)
  ).map((frameworkClass) => frameworkClass.name);
}

/** Float stream metrics available on at least one configured sensor of the given class. */
export function getSensorDrawerFloatMetrics(portRows: DriveUnitPortOccupancy[], projectCode: string, sensorClassName: string): string[] {
  const sections = buildElectronicsSections(sensorPortRows(portRows), projectCode, electronicsDrawerKindForSensorClass(sensorClassName));

  const available = new Set<string>();
  for (const section of sections) {
    for (const widget of section.widgets) {
      if (widget.type === SENSOR_WIDGET_TYPE) {
        available.add(streamMetricSlug(widget.dataKey));
      }
    }
  }

  const order = getFrameworkSensorFloatMetricOrder(sensorClassName);
  const extras = [...available].filter((metric) => !order.includes(metric)).sort();
  return [...order.filter((metric) => available.has(metric)), ...extras];
}

/** Boolean stream metrics available on at least one configured sensor of the given class. */
export function getSensorDrawerBoolMetrics(portRows: DriveUnitPortOccupancy[], projectCode: string, sensorClassName: string): string[] {
  const sections = buildElectronicsSections(sensorPortRows(portRows), projectCode, electronicsDrawerKindForSensorClass(sensorClassName));

  const available = new Set<string>();
  for (const section of sections) {
    for (const widget of section.widgets) {
      if (widget.type === BOOLEAN_WIDGET_TYPE) {
        available.add(streamMetricSlug(widget.dataKey));
      }
    }
  }

  const order = getFrameworkSensorBoolMetricOrder(sensorClassName);
  const extras = [...available].filter((metric) => !order.includes(metric)).sort();
  return [...order.filter((metric) => available.has(metric)), ...extras];
}

/** Stream key for one metric on a sensor drive-unit port, or null when unavailable. */
export function sensorStreamKeyForPortAndMetric(
  portRows: DriveUnitPortOccupancy[],
  projectCode: string,
  port: number,
  metric: string,
  sensorClassName: string
): string | null {
  const row = portRows.find((entry) => entry.driveUnitPort === port);
  if (!row) return null;

  const section = buildElectronicsSections([row], projectCode, electronicsDrawerKindForSensorClass(sensorClassName))[0];
  return streamKeyFromElectronicsSection(section, metric, [SENSOR_WIDGET_TYPE, BOOLEAN_WIDGET_TYPE]);
}

/** Collapsible sensor float-metric groups for the sensor-data drawer (one sensor type). */
export function buildSensorDrawerMetricCollapsibleGroups(
  portRows: DriveUnitPortOccupancy[],
  projectCode: string,
  sensorClassName: string
): DrawerMetricCollapsibleGroup[] {
  const sensorPickerEntries = getSensorPickerEntries(portRows, projectCode, sensorClassName);
  if (sensorPickerEntries.length === 0) return [];

  const floatMetrics = getSensorDrawerFloatMetrics(portRows, projectCode, sensorClassName);
  const allPorts = sensorPickerEntries.map((entry) => entry.port);

  return floatMetrics
    .map((metric) => {
      const baseDataKey = sensorStreamKeyForPortAndMetric(portRows, projectCode, allPorts[0], metric, sensorClassName);
      if (!baseDataKey) return null;

      const prefix = streamPrefixFromSensorKey(baseDataKey);
      const headerItem: DrawerCollapsibleWidget = {
        id: `sensor-drawer-${sensorClassName}-${metric}-all`,
        type: SENSOR_WIDGET_TYPE,
        dataKey: baseDataKey,
        title: sensorMetricLabel(prefix, metric),
        minH: 3,
        minW: 4,
        options: { selectedSensorPorts: allPorts, sensorClassName, allSensorPorts: true },
      };

      const individualItems: DrawerCollapsibleWidget[] = sensorPickerEntries.map((entry) => ({
        id: `sensor-drawer-${sensorClassName}-${metric}-${entry.port}`,
        type: SENSOR_WIDGET_TYPE,
        dataKey: sensorStreamKeyForPortAndMetric(portRows, projectCode, entry.port, metric, sensorClassName) ?? baseDataKey,
        title: sensorPickerPortLabel(entry),
        minH: 3,
        minW: 4,
        options: { selectedSensorPorts: [entry.port], sensorClassName },
      }));

      return { metric, data: [headerItem, ...individualItems] };
    })
    .filter((group): group is DrawerMetricCollapsibleGroup => group !== null);
}

/** Collapsible sensor boolean-metric groups for the sensor-data drawer (one sensor type). */
export function buildSensorDrawerBooleanCollapsibleGroups(
  portRows: DriveUnitPortOccupancy[],
  projectCode: string,
  sensorClassName: string
): DrawerMetricCollapsibleGroup[] {
  const sensorPickerEntries = getSensorPickerEntries(portRows, projectCode, sensorClassName);
  if (sensorPickerEntries.length === 0) return [];

  const boolMetrics = getSensorDrawerBoolMetrics(portRows, projectCode, sensorClassName);
  const allPorts = sensorPickerEntries.map((entry) => entry.port);

  return boolMetrics
    .map((metric) => {
      const baseDataKey = sensorStreamKeyForPortAndMetric(portRows, projectCode, allPorts[0], metric, sensorClassName);
      if (!baseDataKey) return null;

      const prefix = streamPrefixFromSensorKey(baseDataKey);
      const headerItem: DrawerCollapsibleWidget = {
        id: `sensor-drawer-bool-${sensorClassName}-${metric}-all`,
        type: BOOLEAN_WIDGET_TYPE,
        dataKey: baseDataKey,
        title: sensorMetricLabel(prefix, metric),
        minH: 2,
        minW: 4,
        options: { selectedSensorPorts: allPorts, sensorClassName, allSensorPorts: true },
      };

      const individualItems: DrawerCollapsibleWidget[] = sensorPickerEntries.map((entry) => ({
        id: `sensor-drawer-bool-${sensorClassName}-${metric}-${entry.port}`,
        type: BOOLEAN_WIDGET_TYPE,
        dataKey: sensorStreamKeyForPortAndMetric(portRows, projectCode, entry.port, metric, sensorClassName) ?? baseDataKey,
        title: sensorPickerPortLabel(entry),
        minH: 1,
        minW: 2,
        options: { selectedSensorPorts: [entry.port], sensorClassName, sensorVariableName: entry.variableName },
      }));

      return { metric, data: [headerItem, ...individualItems] };
    })
    .filter((group): group is DrawerMetricCollapsibleGroup => group !== null);
}

/** Flat widget catalog for sensor-data drawer draggables (used by tests and layout sync). */
export function buildSensorDataDrawerWidgetCatalog(portRows: DriveUnitPortOccupancy[], projectCode: string): DrawerWidgetCatalogSignature[] {
  const catalog: DrawerWidgetCatalogSignature[] = [];

  for (const sensorClassName of getConfiguredSensorClassNames(portRows, projectCode)) {
    for (const group of buildSensorDrawerMetricCollapsibleGroups(portRows, projectCode, sensorClassName)) {
      catalog.push(...drawerWidgetCatalogEntries(group.data));
    }
    for (const group of buildSensorDrawerBooleanCollapsibleGroups(portRows, projectCode, sensorClassName)) {
      catalog.push(...drawerWidgetCatalogEntries(group.data));
    }
  }

  return catalog;
}

/** Maps drive-unit port → sensor Python variable name for one sensor class. */
export function buildSensorVariableNameByPort(
  driveUnitPorts: DriveUnitPortOccupancy[],
  projectCode: string,
  sensorClassName: string
): Map<number, string> {
  return new Map(getSensorPickerEntries(driveUnitPorts, projectCode, sensorClassName).map((entry) => [entry.port, entry.variableName]));
}

/** Maps drive-unit port → other-electronics Python variable name (e.g. `BitAdapter` on `motor_port`). */
export function buildOtherElectronicsVariableNameByPort(driveUnitPorts: DriveUnitPortOccupancy[], projectCode: string): Map<number, string> {
  return new Map(getOtherPickerEntries(driveUnitPorts, projectCode).map((entry) => [entry.port, entry.variableName]));
}

function electronicsDrawerKindForOtherClass(otherClassName: string): ElectronicsDrawerKind {
  return {
    includedClassNames: new Set([otherClassName]),
    floatWidgetType: OTHER_WIDGET_TYPE,
    floatTitle: otherWidgetDisplayTitle,
    floatWidgetStyle: 'number',
    booleanTitle: sensorBooleanTitleFromStreamKey,
    booleanVariableOptionKey: 'otherVariableName',
  };
}

/** `motor_port` rows for one framework other-electronics class (e.g. all `BitAdapter` instances). */
export function getOtherPickerEntries(driveUnitPorts: DriveUnitPortOccupancy[], projectCode: string, otherClassName?: string): OtherPickerEntry[] {
  const kind = otherClassName ? electronicsDrawerKindForOtherClass(otherClassName) : ELECTRONIC_OTHER_ELECTRONICS_DRAWER_KIND;
  return buildElectronicsSections(motorRows(driveUnitPorts), projectCode, kind).map((section) => {
    const displayTitle = motorPortDisplayTitle(section.port, section.variableName);
    return {
      port: section.port,
      variableName: section.variableName,
      otherType: section.deviceType,
      sectionTitle: displayTitle,
      displayTitle,
    };
  });
}

/** Framework other-electronics classes with at least one configured `motor_port` row in project code. */
export function getConfiguredOtherClassNames(portRows: DriveUnitPortOccupancy[], projectCode: string): string[] {
  const configured = new Set(
    buildElectronicsSections(motorRows(portRows), projectCode, ELECTRONIC_OTHER_ELECTRONICS_DRAWER_KIND).map((section) => section.deviceType)
  );

  return FrameworkClasses.filter(
    (frameworkClass) => frameworkClass.partType === FrameworkClassPartType.Other && configured.has(frameworkClass.name)
  ).map((frameworkClass) => frameworkClass.name);
}

function getFrameworkOtherFloatMetricOrder(otherClassName: string): string[] {
  const frameworkClass = FrameworkClasses.find((fc) => fc.name === otherClassName);
  if (!frameworkClass?.streamData?.length) return [];

  const metrics: string[] = [];
  for (const entry of frameworkClass.streamData) {
    if (!isFrameworkFloatStreamType(entry.type)) continue;
    const match = entry.name.match(SENSOR_STREAM_PORT_TEMPLATE_RE);
    if (match?.[1]) metrics.push(match[1]);
  }
  return metrics;
}

/** Float stream metrics available on at least one configured other-electronics instance of the given class. */
export function getOtherDrawerFloatMetrics(portRows: DriveUnitPortOccupancy[], projectCode: string, otherClassName: string): string[] {
  const sections = buildElectronicsSections(motorRows(portRows), projectCode, electronicsDrawerKindForOtherClass(otherClassName));

  const available = new Set<string>();
  for (const section of sections) {
    for (const widget of section.widgets) {
      if (widget.type === OTHER_WIDGET_TYPE) {
        available.add(streamMetricSlug(widget.dataKey));
      }
    }
  }

  const order = getFrameworkOtherFloatMetricOrder(otherClassName);
  const extras = [...available].filter((metric) => !order.includes(metric)).sort();
  return [...order.filter((metric) => available.has(metric)), ...extras];
}

function parseOtherElectronicsStreamKey(sensorKey: string): { prefix: string; port: number; metric: string } | null {
  if (!otherClassNameFromStreamKey(sensorKey)) return null;
  const port = streamPortFromSensorKey(sensorKey);
  if (port === null) return null;
  const prefix = sensorKey.split('.')[0] ?? '';
  return { prefix, port, metric: streamMetricSlug(sensorKey) };
}

/** Builds stream keys for the same metric/prefix as `baseSensorKey` on each drive-unit port. */
export function otherStreamKeysForPorts(baseSensorKey: string, ports: readonly number[]): string[] {
  const parsed = parseOtherElectronicsStreamKey(baseSensorKey);
  if (!parsed) return ports.length ? [baseSensorKey] : [];

  const uniquePorts = [...new Set(ports)].filter((port) => Number.isFinite(port)).sort((a, b) => a - b);
  return uniquePorts.map((port) => `${parsed.prefix}.port${port}.${parsed.metric}`);
}

/** Drive-unit ports referenced by other-electronics stream keys (optionally filtered to one metric). */
export function otherPortsFromStreamKeys(sensorKeys: readonly string[], baseDataKey?: string): number[] {
  const metric = baseDataKey?.trim() ? streamMetricSlug(baseDataKey) : undefined;
  const ports = sensorKeys
    .filter((key) => {
      if (!isOtherElectronicsStreamSensorKey(key)) return false;
      if (metric === undefined) return true;
      return streamMetricSlug(key) === metric;
    })
    .map((key) => streamPortFromSensorKey(key))
    .filter((port): port is number => port !== null && Number.isFinite(port));
  return [...new Set(ports)].sort((a, b) => a - b);
}

export function defaultSelectedOtherPorts(baseSensorKey: string, availablePorts: readonly number[]): number[] {
  return defaultSelectedDriveUnitPorts(baseSensorKey, availablePorts);
}

/** Stream key for one metric on an other-electronics drive-unit port, or null when unavailable. */
export function otherStreamKeyForPortAndMetric(
  portRows: DriveUnitPortOccupancy[],
  projectCode: string,
  port: number,
  metric: string,
  otherClassName: string
): string | null {
  const row = portRows.find((entry) => entry.driveUnitPort === port);
  if (!row) return null;

  const section = buildElectronicsSections([row], projectCode, electronicsDrawerKindForOtherClass(otherClassName))[0];
  return streamKeyFromElectronicsSection(section, metric, [OTHER_WIDGET_TYPE]);
}

/** Collapsible other-electronics float-metric groups for the other-data drawer (one class). */
export function buildOtherDrawerMetricCollapsibleGroups(
  portRows: DriveUnitPortOccupancy[],
  projectCode: string,
  otherClassName: string
): DrawerMetricCollapsibleGroup[] {
  const otherPickerEntries = getOtherPickerEntries(portRows, projectCode, otherClassName);
  if (otherPickerEntries.length === 0) return [];

  const floatMetrics = getOtherDrawerFloatMetrics(portRows, projectCode, otherClassName);
  const allPorts = otherPickerEntries.map((entry) => entry.port);

  return floatMetrics
    .map((metric) => {
      const baseDataKey = otherStreamKeyForPortAndMetric(portRows, projectCode, allPorts[0], metric, otherClassName);
      if (!baseDataKey) return null;

      const prefix = streamPrefixFromSensorKey(baseDataKey);
      const headerItem: DrawerCollapsibleWidget = {
        id: `other-drawer-${otherClassName}-${metric}-all`,
        type: OTHER_WIDGET_TYPE,
        dataKey: baseDataKey,
        title: otherMetricLabel(prefix, metric),
        minH: 3,
        minW: 4,
        style: 'number',
        options: { selectedOtherPorts: allPorts, otherClassName, allOtherPorts: true },
      };

      const individualItems: DrawerCollapsibleWidget[] = otherPickerEntries.map((entry) => ({
        id: `other-drawer-${otherClassName}-${metric}-${entry.port}`,
        type: OTHER_WIDGET_TYPE,
        dataKey: otherStreamKeyForPortAndMetric(portRows, projectCode, entry.port, metric, otherClassName) ?? baseDataKey,
        title: otherPickerPortLabel(entry),
        minH: 3,
        minW: 4,
        style: 'number',
        options: { selectedOtherPorts: [entry.port], otherClassName, otherVariableName: entry.variableName },
      }));

      return { metric, data: [headerItem, ...individualItems] };
    })
    .filter((group): group is DrawerMetricCollapsibleGroup => group !== null);
}

/** Maps drive-unit port → other-electronics drawer/chart port label for one class. */
export function buildOtherElectronicsChartSeriesLabelByPort(
  driveUnitPorts: DriveUnitPortOccupancy[],
  projectCode: string,
  otherClassName?: string
): Map<number, string> {
  return new Map(
    getOtherPickerEntries(driveUnitPorts, projectCode, otherClassName).map((entry) => [
      entry.port,
      entry.displayTitle ?? motorPortDisplayTitle(entry.port, entry.variableName),
    ])
  );
}

/** Resolves persisted other-electronics ports and stream keys when dragging an `other` widget onto the dashboard. */
export function resolveOtherElectronicsWidgetDrop(
  dataKeyStr: string,
  widgetOptions: WidgetOptions | undefined
): { dropOptions: Pick<WidgetOptions, 'selectedOtherPorts' | 'otherClassName'>; sensorKeys: string[] } | null {
  const parsed = parseOtherElectronicsStreamKey(dataKeyStr);
  const otherClassName = widgetOptions?.otherClassName ?? otherClassNameFromStreamKey(dataKeyStr);
  if (!parsed || !otherClassName) return null;

  const portsFromOptions = widgetOptions?.selectedOtherPorts?.filter((port): port is number => Number.isFinite(port));
  const selectedPorts = portsFromOptions?.length ? [...new Set(portsFromOptions)].sort((a, b) => a - b) : [parsed.port];

  return {
    dropOptions: { selectedOtherPorts: selectedPorts, otherClassName },
    sensorKeys: otherStreamKeysForPorts(dataKeyStr, selectedPorts),
  };
}

/** Flat widget catalog for other-data drawer draggables (used by drawer contract tests). */
export function buildOtherDataDrawerWidgetCatalog(portRows: DriveUnitPortOccupancy[], projectCode: string): DrawerWidgetCatalogSignature[] {
  const catalog: DrawerWidgetCatalogSignature[] = [];

  for (const otherClassName of getConfiguredOtherClassNames(portRows, projectCode)) {
    for (const group of buildOtherDrawerMetricCollapsibleGroups(portRows, projectCode, otherClassName)) {
      catalog.push(...drawerWidgetCatalogEntries(group.data));
    }
  }

  return catalog;
}
